import React, { useEffect, useState } from 'react';
import { MessageCircleIcon, SendIcon } from 'lucide-react';
import { DS } from '../../ds';
const QA_HEADER_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/39ed121d-1fe9-4ece-a113-c0ab633ed16a.jpg';
const guidelines = ['Sé respetuoso con otros profesionales', 'Comparte experiencias clínicas reales', 'Haz preguntas específicas sobre ortodoncia invisible', 'No promuevas otros productos competidores', 'Las respuestas son validadas por el equipo Alinova'];
const STORAGE_KEY = 'alinova_blog_questions';
interface StoredReply {
  id: number;
  text: string;
  date: string;
}
interface StoredQuestion {
  id: number;
  name: string;
  question: string;
  date: string;
  replies: StoredReply[];
}
function loadQuestions(): StoredQuestion[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveQuestions(questions: StoredQuestion[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(questions));
  } catch {
    // localStorage puede no estar disponible (modo privado); no bloqueamos la UI.
  }
}
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '13px 16px',
  border: `1.5px solid ${DS.color.border}`,
  borderRadius: '12px',
  fontFamily: DS.font.body,
  fontSize: '14px',
  color: DS.color.ink,
  boxSizing: 'border-box',
  outline: 'none',
  transition: `border-color 0.2s ${DS.ease.smooth}`
};
function focusAqua(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
  e.currentTarget.style.borderColor = DS.color.aqua;
}
function blurBorder(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
  e.currentTarget.style.borderColor = DS.color.border;
}
export const BlogQA = () => {
  const [questions, setQuestions] = useState<StoredQuestion[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    question: ''
  });
  const [replyText, setReplyText] = useState<Record<number, string>>({});
  const [justSubmittedId, setJustSubmittedId] = useState<number | null>(null);
  const [openReplyFor, setOpenReplyFor] = useState<number | null>(null);
  useEffect(() => {
    setQuestions(loadQuestions());
  }, []);
  useEffect(() => {
    if (justSubmittedId === null) return;
    const timeout = window.setTimeout(() => setJustSubmittedId(null), 1600);
    return () => window.clearTimeout(timeout);
  }, [justSubmittedId]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newQuestion: StoredQuestion = {
      ...formData,
      date: new Date().toLocaleDateString('es-CO'),
      id: Date.now(),
      replies: []
    };
    const updated = [newQuestion, ...questions];
    setQuestions(updated);
    saveQuestions(updated);
    setFormData({
      name: '',
      question: ''
    });
    setJustSubmittedId(newQuestion.id);
  };
  const handleAddReply = (questionId: number) => {
    const text = replyText[questionId]?.trim();
    if (!text) return;
    const updated = questions.map(q => q.id === questionId ? {
      ...q,
      replies: [...q.replies, {
        id: Date.now(),
        text,
        date: new Date().toLocaleDateString('es-CO')
      }]
    } : q);
    setQuestions(updated);
    saveQuestions(updated);
    setReplyText(prev => ({
      ...prev,
      [questionId]: ''
    }));
    setOpenReplyFor(null);
  };
  return <div data-id="element-283" data-mp="2mdwnrfxcgh1r">
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 200px)',
      gap: '24px',
      alignItems: 'center',
      marginBottom: '32px'
    }} className="qa-header-grid" data-id="element-284" data-mp="2mdmvflottxsw">
        <div data-id="element-285" data-mp="3t022ejdprutu">
          <h2 style={{
          fontFamily: DS.font.heading,
          fontSize: '22px',
          fontWeight: 700,
          color: DS.color.ink,
          margin: '0 0 8px'
        }} data-id="element-286" data-mp="3szinzcn9vokk">
            Preguntas &amp; Respuestas
          </h2>
          <p style={{
          fontFamily: DS.font.body,
          fontSize: '14px',
          color: DS.color.body,
          margin: 0,
          maxWidth: '520px'
        }} data-id="element-287" data-mp="1n7giilx2h8b">
            Haz preguntas, comparte conocimiento y conecta con otros
            profesionales en ortodoncia invisible.
          </p>
        </div>

        <div style={{
        width: '100%',
        aspectRatio: '16 / 10',
        borderRadius: DS.radius.cardLg,
        overflow: 'hidden',
        background: DS.color.linen
      }} className="qa-header-image" data-id="element-288" data-mp="3f0iy61o0x3mt">
          <img src={QA_HEADER_IMAGE} alt="Profesionales de la salud dental discutiendo un caso clínico" style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }} data-id="element-289" data-mp="11v5hnk9l98wg" />
        </div>
      </div>

      <div style={{
      padding: '24px',
      background: DS.color.mint,
      borderRadius: DS.radius.cardLg,
      marginBottom: '40px'
    }} data-id="element-290" data-mp="rxzrce8stlxa">
        <h3 style={{
        fontFamily: DS.font.heading,
        fontSize: '15px',
        fontWeight: 700,
        color: DS.color.tealDeep,
        margin: '0 0 14px'
      }} data-id="element-291" data-mp="1kmet3z2x67qo">
          Normas de la comunidad
        </h3>
        <ul style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'grid',
        gap: '10px'
      }} data-id="element-292" data-mp="1fudliqgp556z">
          {guidelines.map(rule => <li key={rule} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          fontFamily: DS.font.body,
          fontSize: '14px',
          color: DS.color.tealDeep
        }} data-id="element-293" data-mp="28bzq3lvwr5iy">
              <span aria-hidden="true" style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: DS.color.tealDeep,
            marginTop: '7px',
            flexShrink: 0
          }} data-id="element-294" data-mp="294ozacd6q9d5" />
              {rule}
            </li>)}
        </ul>
      </div>

      {/* Formulario de preguntas */}
      <div style={{
      padding: '32px',
      background: '#fff',
      border: `1px solid ${DS.color.border}`,
      borderRadius: DS.radius.cardLg,
      marginBottom: '40px'
    }} data-id="element-295" data-mp="1lgnthb7e571f">
        <h3 style={{
        fontFamily: DS.font.heading,
        fontSize: '18px',
        fontWeight: 700,
        color: DS.color.ink,
        margin: '0 0 6px'
      }} data-id="element-296" data-mp="1bi86nkfecjxq">
          Haz tu pregunta rápidamente
        </h3>
        <p style={{
        fontFamily: DS.font.body,
        fontSize: '13px',
        color: DS.color.muted,
        margin: '0 0 22px'
      }} data-id="element-297" data-mp="yn2ncuey08l">
          Sin necesidad de registro. Solo tu nombre y tu pregunta.
        </p>

        <form onSubmit={handleSubmit} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px'
      }} data-id="element-298" data-mp="34h6l5nnahrq7">
          <input type="text" name="name" placeholder="Tu nombre completo" value={formData.name} onChange={handleChange} required style={inputStyle} onFocus={focusAqua} onBlur={blurBorder} data-id="element-299" data-mp="3dga6ky9xij7v" />
          <textarea name="question" placeholder="Tu pregunta sobre ortodoncia invisible..." value={formData.question} onChange={handleChange} required rows={4} style={{
          ...inputStyle,
          resize: 'vertical',
          minHeight: '110px'
        }} onFocus={focusAqua} onBlur={blurBorder} data-id="element-300" data-mp="2ppxq2z0mbe5o" />

          <button type="submit" style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: '#00AAD4',
          boxShadow: '0 8px 26px rgba(0, 170, 212, 0.42)',
          color: '#fff',
          border: 'none',
          padding: '12px 28px',
          borderRadius: DS.radius.pill,
          fontFamily: DS.font.body,
          fontSize: '14px',
          fontWeight: 700,
          cursor: 'pointer',
          transition: `background-color 0.22s ${DS.ease.smooth}, box-shadow 0.22s ${DS.ease.smooth}`
        }} onMouseEnter={e => {
          e.currentTarget.style.background = '#0099CC';
          e.currentTarget.style.boxShadow = '0 12px 34px rgba(0, 170, 212, 0.55)';
        }} onMouseLeave={e => {
          e.currentTarget.style.background = '#00AAD4';
          e.currentTarget.style.boxShadow = '0 8px 26px rgba(0, 170, 212, 0.42)';
        }} data-id="element-301" data-mp="1o2hqgw4x44v0">
            <SendIcon size={14} aria-hidden="true" data-id="element-302" data-mp="2gnn439zd3atk" />
            Enviar pregunta
          </button>
        </form>
      </div>

      {/* Preguntas de la comunidad */}
      <div data-id="element-303" data-mp="27o9u8g7bsk8b">
        <h3 style={{
        fontFamily: DS.font.heading,
        fontSize: '18px',
        fontWeight: 700,
        color: DS.color.ink,
        margin: '0 0 6px'
      }} data-id="element-304" data-mp="27ojk83sdere0">
          Preguntas de la comunidad
        </h3>
        <p style={{
        fontFamily: DS.font.body,
        fontSize: '13px',
        color: DS.color.muted,
        margin: '0 0 20px'
      }} data-id="element-305" data-mp="1xs6jilyerd9r">
          Respuestas de profesionales y del equipo Alinova.
        </p>

        {questions.length === 0 ? <div style={{
        background: '#fff',
        border: `1px dashed ${DS.color.border}`,
        borderRadius: DS.radius.cardLg,
        padding: '48px 24px',
        textAlign: 'center',
        fontFamily: DS.font.body,
        fontSize: '14px',
        color: DS.color.muted
      }} data-id="element-306" data-mp="3uf0pd0aaheqk">
            Aún no hay preguntas. ¡Sé el primero en preguntar!
          </div> : <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '18px'
      }} data-id="element-307" data-mp="3aizmwqu7mdk">
            {questions.map(q => <div key={q.id} style={{
          background: q.id === justSubmittedId ? DS.color.mint : '#fff',
          border: `1px solid ${q.id === justSubmittedId ? DS.color.aqua : DS.color.border}`,
          borderRadius: DS.radius.cardLg,
          padding: '22px',
          transition: `background-color 0.5s ${DS.ease.smooth}, border-color 0.5s ${DS.ease.smooth}`
        }} data-id="element-308" data-mp="882i68ukilbb">
                <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            flexWrap: 'wrap',
            gap: '8px'
          }} data-id="element-309" data-mp="2q7ttaqfucj5a">
                  <strong style={{
              fontFamily: DS.font.body,
              fontSize: '14px',
              color: DS.color.ink
            }} data-id="element-310" data-mp="2bjqjf5yognb7">
                    {q.name}
                  </strong>
                  <span style={{
              fontFamily: DS.font.body,
              fontSize: '12px',
              color: DS.color.muted
            }} data-id="element-311" data-mp="3hzj545umc905">
                    {q.date}
                  </span>
                </div>

                <p style={{
            fontFamily: DS.font.body,
            fontSize: '15px',
            color: DS.color.body,
            lineHeight: 1.6,
            margin: '0 0 16px'
          }} data-id="element-312" data-mp="2olrrjxrxcwdl">
                  {q.question}
                </p>

                {q.replies.length > 0 && <div style={{
            borderLeft: `3px solid ${DS.color.aqua}`,
            background: DS.color.ivory,
            borderRadius: '0 10px 10px 0',
            padding: '14px 16px',
            marginBottom: '16px',
            display: 'grid',
            gap: '10px'
          }} data-id="element-313" data-mp="25v0bkddfm7q8">
                    <span style={{
              fontFamily: DS.font.body,
              fontSize: '12px',
              fontWeight: 700,
              color: DS.color.tealDeep,
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }} data-id="element-314" data-mp="1al3thwn6rfdk">
                      {q.replies.length}{' '}
                      {q.replies.length === 1 ? 'respuesta' : 'respuestas'}
                    </span>
                    {q.replies.map(reply => <div key={reply.id} data-id="element-315" data-mp="hwziqwprcc40">
                        <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '4px'
              }} data-id="element-316" data-mp="hvoiv7zfnoi6">
                          <strong style={{
                  fontFamily: DS.font.body,
                  fontSize: '13px',
                  color: DS.color.tealDeep
                }} data-id="element-317" data-mp="40cf6b3aunp4">
                            Alinova Team
                          </strong>
                          <span style={{
                  fontFamily: DS.font.body,
                  fontSize: '11px',
                  color: DS.color.muted
                }} data-id="element-318" data-mp="1z7y98o9ar453">
                            {reply.date}
                          </span>
                        </div>
                        <p style={{
                fontFamily: DS.font.body,
                fontSize: '14px',
                color: DS.color.body,
                lineHeight: 1.55,
                margin: 0
              }} data-id="element-319" data-mp="1gbga3ls4c1yt">
                          {reply.text}
                        </p>
                      </div>)}
                  </div>}

                <div style={{
            borderTop: `1px solid ${DS.color.border}`,
            paddingTop: '14px'
          }} data-id="element-320" data-mp="3tmhlhuxvq0kw">
                  {openReplyFor === q.id ? <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }} data-id="element-321" data-mp="2elwis128f4o">
                      <textarea autoFocus placeholder="Escribe una respuesta..." value={replyText[q.id] || ''} onChange={e => setReplyText(prev => ({
                ...prev,
                [q.id]: e.target.value
              }))} rows={2} style={{
                ...inputStyle,
                resize: 'vertical',
                minHeight: '64px',
                fontSize: '13px'
              }} onFocus={focusAqua} onBlur={blurBorder} data-id="element-322" data-mp="gdd8o8wd3qq6" />
                      <div style={{
                display: 'flex',
                gap: '10px'
              }} data-id="element-323" data-mp="101cwn92j2glg">
                        <button type="button" onClick={() => handleAddReply(q.id)} disabled={!replyText[q.id]?.trim()} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  background: DS.color.teal,
                  border: 'none',
                  color: '#fff',
                  padding: '9px 18px',
                  borderRadius: DS.radius.pill,
                  fontFamily: DS.font.body,
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: replyText[q.id]?.trim() ? 'pointer' : 'not-allowed',
                  opacity: replyText[q.id]?.trim() ? 1 : 0.5
                }} data-id="element-324" data-mp="1517nxdq7t871">
                          <MessageCircleIcon size={14} aria-hidden="true" data-id="element-325" data-mp="3mzfj65tquwzr" />
                          Publicar respuesta
                        </button>
                        <button type="button" onClick={() => setOpenReplyFor(null)} style={{
                  background: 'transparent',
                  border: 'none',
                  color: DS.color.muted,
                  padding: '9px 12px',
                  fontFamily: DS.font.body,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }} data-id="element-326" data-mp="3d9drz563gygm">
                          Cancelar
                        </button>
                      </div>
                    </div> : <button type="button" onClick={() => setOpenReplyFor(q.id)} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              background: 'transparent',
              border: `1.5px solid ${DS.color.teal}`,
              color: DS.color.teal,
              padding: '9px 18px',
              borderRadius: DS.radius.pill,
              fontFamily: DS.font.body,
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              transition: `background-color 0.2s ${DS.ease.smooth}`
            }} onMouseEnter={e => {
              e.currentTarget.style.background = DS.color.mint;
            }} onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
            }} data-id="element-327" data-mp="w3h9i9wojxc">
                      <MessageCircleIcon size={14} aria-hidden="true" data-id="element-328" data-mp="201z5397xch92" />
                      Responder
                    </button>}
                </div>
              </div>)}
          </div>}
      </div>

      <style data-id="element-329" data-mp="17kn26yn4ecim">
        {`
          @media (max-width: 720px) {
            .qa-header-grid {
              grid-template-columns: 1fr !important;
            }
            .qa-header-image {
              display: none;
            }
          }
        `}
      </style>
    </div>;
};